import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, ListChecks, Paperclip, Download, Image, RefreshCw, Pencil, Save, Trash2 } from 'lucide-react';
import { VerticalTimeline } from '../VerticalTimeline';
import { Modal } from '../Modal';

interface Attachment {
  title: string;
  size: number;
  url: string;
}

interface TaskContentProps {
  summary: string | null;
  summaryError: string | null;
  isLoadingSummary: boolean;
  timelineEvents: Array<{ date: string; event: string }>;
  timelineError: string | null;
  isLoadingTimeline: boolean;
  results: string[] | null;
  resultsError: string | null;
  isLoadingResults: boolean;
  attachments?: Attachment[];
  hideAttachments: boolean;
  isEnglish: boolean;
  onRefreshSummary?: () => void;
  onRefreshTimeline?: () => void;
  onRefreshResults?: () => void;
  onUpdateSummary?: (newSummary: string) => void;
  onUpdateResults?: (newResults: string[]) => void;
  onUpdateTimeline?: (newEvents: Array<{ date: string; event: string }>) => void;
  onUpdateAttachments?: (newAttachments: Attachment[]) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const isImageUrl = (url: string): boolean => {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
};

export const TaskContent: React.FC<TaskContentProps> = ({
  summary,
  summaryError,
  isLoadingSummary,
  timelineEvents,
  timelineError,
  isLoadingTimeline,
  results,
  resultsError,
  isLoadingResults,
  attachments = [],
  hideAttachments,
  isEnglish,
  onRefreshSummary,
  onRefreshTimeline,
  onRefreshResults,
  onUpdateSummary,
  onUpdateResults,
  onUpdateTimeline,
  onUpdateAttachments,
}) => {
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [editedSummary, setEditedSummary] = useState('');
  const [editedResults, setEditedResults] = useState<string[]>([]);
  const [isSummaryEditing, setIsSummaryEditing] = useState(false);
  const [isResultsEditing, setIsResultsEditing] = useState(false);
  const [editableAttachments, setEditableAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    setEditableAttachments(attachments);
  }, [attachments]);

  const startSummaryEditing = useCallback(() => {
    setEditedSummary(summary || '');
    setIsSummaryEditing(true);
  }, [summary]);

  const startResultsEditing = useCallback(() => {
    setEditedResults(results || []);
    setIsResultsEditing(true);
  }, [results]);

  const handleSummaryChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedSummary(e.target.value);
  }, []);

  const handleResultChange = useCallback((index: number, value: string) => {
    setEditedResults(prev => {
      const newResults = [...prev];
      newResults[index] = value;
      return newResults;
    });
  }, []);

  const handleDeleteResult = useCallback((index: number) => {
    setEditedResults(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSaveSummary = useCallback(() => {
    if (onUpdateSummary) {
      onUpdateSummary(editedSummary);
    }
    setIsSummaryEditing(false);
  }, [editedSummary, onUpdateSummary]);

  const handleSaveResults = useCallback(() => {
    if (onUpdateResults) {
      onUpdateResults(editedResults);
    }
    setIsResultsEditing(false);
  }, [editedResults, onUpdateResults]);

  const handleDeleteAttachment = useCallback((attachment: Attachment) => {
    setEditableAttachments(prev => {
      const newAttachments = prev.filter(a => a !== attachment);
      if (onUpdateAttachments) {
        onUpdateAttachments(newAttachments);
      }
      return newAttachments;
    });
  }, [onUpdateAttachments]);

  const imageAttachments = editableAttachments.filter(att => isImageUrl(att.url));
  const nonImageAttachments = editableAttachments.filter(att => !isImageUrl(att.url));

  const shouldShowTimeline = isLoadingTimeline || timelineEvents.length > 0 || timelineError;
  const shouldShowResults = isLoadingResults || results?.length > 0 || resultsError;

  return (
    <div className="prose max-w-none space-y-8 print:space-y-4">
      {/* Summary Section */}
      <div>
        <div className="flex items-center justify-between mb-4 print:mb-2">
          <h2 className="text-xl font-semibold flex items-center gap-2 print:text-lg">
            <Sparkles className="w-5 h-5 text-blue-600" />
            {isEnglish ? 'Objective Summary' : 'Resumo do Objetivo'}
          </h2>
          <div className="flex items-center gap-2 print:hidden">
            {onRefreshSummary && !isSummaryEditing && (
              <button
                onClick={onRefreshSummary}
                disabled={isLoadingSummary}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                title={isEnglish ? 'Regenerate summary' : 'Regerar resumo'}
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingSummary ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={() => isSummaryEditing ? handleSaveSummary() : startSummaryEditing()}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {isSummaryEditing ? (
                <Save className="w-4 h-4" />
              ) : (
                <Pencil className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
        <div className="text-gray-700 bg-blue-50 rounded-lg p-6 print:p-4 print:text-sm">
          {isLoadingSummary ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2">
                {isEnglish ? 'Generating summary with GPT-3.5...' : 'Gerando resumo com GPT-3.5...'}
              </span>
            </div>
          ) : summaryError ? (
            <div className="text-red-600">{summaryError}</div>
          ) : isSummaryEditing ? (
            <textarea
              value={editedSummary}
              onChange={handleSummaryChange}
              className="w-full h-32 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isEnglish ? "Edit the summary..." : "Edite o resumo..."}
            />
          ) : (
            summary || (isEnglish ? 'Waiting for processing...' : 'Aguardando processamento...')
          )}
        </div>
      </div>

      {/* Timeline Section */}
      {shouldShowTimeline && (
        <div className="border-t pt-8 print:pt-4">
          <div className="flex items-center justify-between mb-6 print:mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 print:text-lg">
              <ListChecks className="w-5 h-5 text-blue-600" />
              {isEnglish ? 'Timeline' : 'Linha do Tempo'}
            </h2>
            {onRefreshTimeline && (
              <button
                onClick={onRefreshTimeline}
                disabled={isLoadingTimeline}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors print:hidden"
                title={isEnglish ? 'Refresh timeline' : 'Atualizar linha do tempo'}
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingTimeline ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg p-6 print:p-4">
            <VerticalTimeline
              events={timelineEvents}
              isLoading={isLoadingTimeline}
              error={timelineError}
              isEnglish={isEnglish}
              onUpdateEvents={onUpdateTimeline}
            />
          </div>
        </div>
      )}

      {/* Results Section */}
      {shouldShowResults && (
        <div className="border-t pt-8 print:pt-4">
          <div className="flex items-center justify-between mb-6 print:mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 print:text-lg">
              <ListChecks className="w-5 h-5 text-blue-600" />
              {isEnglish ? 'Achieved Results' : 'Resultados Apurados'}
            </h2>
            <div className="flex items-center gap-2 print:hidden">
              {onRefreshResults && !isResultsEditing && (
                <button
                  onClick={onRefreshResults}
                  disabled={isLoadingResults}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                  title={isEnglish ? 'Refresh results' : 'Atualizar resultados'}
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingResults ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button
                onClick={() => isResultsEditing ? handleSaveResults() : startResultsEditing()}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                {isResultsEditing ? (
                  <Save className="w-4 h-4" />
                ) : (
                  <Pencil className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-6 print:p-4">
            {isLoadingResults ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                <span className="ml-2">
                  {isEnglish ? 'Analyzing results...' : 'Analisando resultados...'}
                </span>
              </div>
            ) : resultsError ? (
              <div className="text-red-600">{resultsError}</div>
            ) : editedResults.length > 0 || results?.length > 0 ? (
              <ul className="space-y-3">
                {(isResultsEditing ? editedResults : results || []).map((result, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-green-600 text-sm font-medium">{index + 1}</span>
                    </span>
                    {isResultsEditing ? (
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          value={result}
                          onChange={(e) => handleResultChange(index, e.target.value)}
                          className="flex-1 p-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <button
                          onClick={() => handleDeleteResult(index)}
                          className="p-2 text-red-600 hover:text-red-800 rounded-md hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-700">{result}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">
                {isEnglish ? 'No results found' : 'Nenhum resultado encontrado'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Non-image attachments - hidden in print */}
      {nonImageAttachments.length > 0 && !hideAttachments && (
        <div className="border-t pt-8 print:hidden">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-blue-600" />
            {isEnglish ? 'Documents' : 'Documentos'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nonImageAttachments.map((attachment, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 flex items-start gap-4">
                <div className="bg-gray-200 rounded p-2 flex-shrink-0">
                  <Paperclip className="w-8 h-8 text-gray-600" />
                </div>
                <div className="flex-grow min-w-0">
                  <h4 className="font-medium text-gray-900 truncate mb-1">{attachment.title}</h4>
                  <p className="text-sm text-gray-500 mb-2">{formatFileSize(attachment.size)}</p>
                  <div className="flex gap-2">
                    <a
                      href={attachment.url}
                      download
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Download className="w-4 h-4" />
                      {isEnglish ? 'Download' : 'Baixar'}
                    </a>
                    <button
                      onClick={() => handleDeleteAttachment(attachment)}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                      {isEnglish ? 'Remove' : 'Remover'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image attachments - always visible in print */}
      {imageAttachments.length > 0 && (!hideAttachments || window.matchMedia('print').matches) && (
        <div className="border-t pt-8 print:pt-4 print:break-before-page">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 print:text-lg print:mb-4">
            <Image className="w-5 h-5 text-blue-600" />
            {isEnglish ? 'Images' : 'Imagens'}
          </h2>
          <div className="grid grid-cols-1 gap-8 print:gap-6">
            {imageAttachments.map((attachment, index) => (
              <figure key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <figcaption className="font-medium text-gray-900">{attachment.title}</figcaption>
                  <button
                    onClick={() => handleDeleteAttachment(attachment)}
                    className="text-gray-400 hover:text-red-600 transition-colors p-1 print:hidden"
                    title="Remover do histórico"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={() => setSelectedAttachment(attachment)}
                  className="w-full cursor-zoom-in print:cursor-default print:hidden"
                >
                  <img
                    src={attachment.url}
                    alt={attachment.title}
                    className="max-w-full h-auto rounded-lg shadow-md print:shadow-none print:max-h-[500px] print:object-contain"
                    loading="lazy"
                  />
                </button>
                <img
                  src={attachment.url}
                  alt={attachment.title}
                  className="hidden print:block max-w-full h-auto rounded-lg print:max-h-[500px] print:object-contain"
                />
              </figure>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={!!selectedAttachment}
        onClose={() => setSelectedAttachment(null)}
        title={selectedAttachment?.title}
      >
        {selectedAttachment && isImageUrl(selectedAttachment.url) && (
          <div className="flex flex-col items-center">
            <img
              src={selectedAttachment.url}
              alt={selectedAttachment.title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="mt-4 flex justify-center gap-4">
              <a
                href={selectedAttachment.url}
                download
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                {isEnglish ? 'Download' : 'Baixar'}
              </a>
              <button
                onClick={() => {
                  handleDeleteAttachment(selectedAttachment);
                  setSelectedAttachment(null);
                }}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {isEnglish ? 'Remove' : 'Remover'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};